/*
 * Copyright 2026 North Carolina State University
 */

/**
 * Formatting utilities for phone numbers and account numbers
 */

import {
  formatInternational,
  formatNational,
  isValidInternationalPhone,
  toE164,
  detectCountry
} from './phoneUtils';

/**
 * Format a phone number for display / typing.
 * Prefers the international format from libphonenumber; falls back to the
 * historical US (###) ###-#### mask for 10-digit numbers that have not been
 * migrated yet.
 */
export const formatPhoneNumber = (value) => {
  if (!value) return '';
  const international = formatInternational(value);
  if (international && international !== String(value)) return international;

  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
  if (digits.length === 0) return '';
  return formatNational(value, detectCountry(value, 'US')) || String(value);
};

/**
 * Handle phone number input - formats as user types
 * @param {string} value - The raw input value
 * @returns {string} - Formatted phone number
 */
export const handlePhoneInput = (value) => {
  return formatPhoneNumber(value);
};

/**
 * Extract raw digits from a formatted phone number
 * @param {string} formattedPhone - The formatted phone number
 * @returns {string} - Just the digits
 */
export const unformatPhoneNumber = (formattedPhone) => {
  if (!formattedPhone) return '';
  const e164 = toE164(formattedPhone);
  if (e164) return e164.replace(/\D/g, '');
  return formattedPhone.replace(/\D/g, '');
};

/**
 * Validate an international (or legacy US 10-digit) phone number.
 */
export const isValidPhoneNumber = (value) => {
  if (!value) return false;
  if (isValidInternationalPhone(value)) return true;
  const digits = String(value).replace(/\D/g, '');
  return digits.length === 10 || (digits.length >= 8 && digits.length <= 15);
};

/**
 * Format an account number to XXXXXX-XXXXX format (6 digits, dash, 5 digits)
 * @param {string} value - The account number input
 * @returns {string} - Formatted account number
 */
export const formatAccountNumber = (value) => {
  if (!value) return '';
  
  // Remove all non-alphanumeric characters except existing dashes
  const cleaned = value.replace(/[^a-zA-Z0-9]/g, '');
  
  // If we have 6 or fewer characters, just return them
  if (cleaned.length <= 6) {
    return cleaned.toUpperCase();
  }
  
  // If we have more than 6 characters, add dash after 6th
  const firstPart = cleaned.slice(0, 6);
  const secondPart = cleaned.slice(6, 11); // Max 5 more characters
  
  return `${firstPart.toUpperCase()}-${secondPart.toUpperCase()}`;
};

/**
 * Handle account number input - formats as user types
 * @param {string} value - The raw input value
 * @returns {string} - Formatted account number
 */
export const handleAccountInput = (value) => {
  return formatAccountNumber(value);
};

/**
 * Extract raw account number without dash
 * @param {string} formattedAccount - The formatted account number
 * @returns {string} - Account number without dash
 */
export const unformatAccountNumber = (formattedAccount) => {
  if (!formattedAccount) return '';
  return formattedAccount.replace(/-/g, '');
};

/**
 * Validate if an account number has the correct format (6 chars + 5 chars = 11 total)
 * @param {string} value - The account number (formatted or unformatted)
 * @returns {boolean} - True if valid account number format
 */
export const isValidAccountNumber = (value) => {
  if (!value) return false;
  const cleaned = value.replace(/[^a-zA-Z0-9]/g, '');
  return cleaned.length === 11;
};

/**
 * Display a phone number - ensures it's in the correct format for display
 * @param {string} phone - The phone number from database
 * @returns {string} - Formatted phone number for display
 */
export const displayPhoneNumber = (phone) => {
  if (!phone) return 'Not provided';
  return formatInternational(phone) || formatPhoneNumber(phone);
};

/**
 * Display an account number - ensures it's in the correct format for display
 * @param {string} account - The account number from database
 * @returns {string} - Formatted account number for display
 */
export const displayAccountNumber = (account) => {
  if (!account) return '';
  return formatAccountNumber(account);
};

/**
 * Safe date formatting utility - handles null, undefined, or invalid dates gracefully
 * @param {string|Date|null|undefined} dateValue - The date value to format
 * @param {string} fallback - Fallback text if date is invalid (default: 'Not set')
 * @returns {string} - Formatted date string (YYYY-MM-DD) or fallback
 */
export const formatDateSafe = (dateValue, fallback = 'Not set') => {
  if (!dateValue) return fallback;
  try {
    if (typeof dateValue === 'string') {
      // Handle ISO string format
      if (dateValue.includes('T')) {
        return dateValue.split('T')[0];
      }
      // Already in date format
      return dateValue;
    }
    if (dateValue instanceof Date) {
      if (isNaN(dateValue.getTime())) return fallback;
      return dateValue.toISOString().split('T')[0];
    }
    return fallback;
  } catch (error) {
    console.warn('Error formatting date:', dateValue, error);
    return fallback;
  }
};

/**
 * Format date for display in a more readable format
 * @param {string|Date|null|undefined} dateValue - The date value to format
 * @param {string} fallback - Fallback text if date is invalid
 * @returns {string} - Formatted date string (e.g., "Jan 15, 2024") or fallback
 */
export const formatDateDisplay = (dateValue, fallback = 'Not set') => {
  if (!dateValue) return fallback;
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return fallback;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    console.warn('Error formatting date for display:', dateValue, error);
    return fallback;
  }
};

/**
 * Safe value accessor - gets nested property values safely
 * @param {Object} obj - The object to access
 * @param {string} path - Dot-separated path to the property
 * @param {*} defaultValue - Default value if property is null/undefined
 * @returns {*} - The property value or default value
 */
export const safeGet = (obj, path, defaultValue = 'N/A') => {
  try {
    if (!obj || !path) return defaultValue;
    const keys = path.split('.');
    let result = obj;
    for (const key of keys) {
      if (result === null || result === undefined) return defaultValue;
      result = result[key];
    }
    return result !== null && result !== undefined ? result : defaultValue;
  } catch (error) {
    return defaultValue;
  }
};
